/**
 * Subsite navigation augmentation.
 *
 * The shared Navigation component builds its links from the CMS
 * `findResult.lang.<lang>.Pages` array. The legacy site showed an
 * "Other Settlements" link (pointing back to the root hub) on each settlement
 * subsite, but the migrated CMS dockets do not carry that page. This helper
 * appends a synthetic page entry so the shared nav renders the link — only on
 * the settlement subsites, never on the root hub.
 *
 * The entry's `PageName` is "/" so the shared nav renders `<a href="/">`, which
 * the site's docket-aware router treats as a docket-boundary navigation and
 * full-loads the root hub.
 *
 * @module subsiteNav
 */

import { currentDocketSlug } from "./resolveRoute";

/**
 * Synthetic "Other Settlements" nav entry linking to the root hub. Only the
 * fields the shared Navigation reads (PageName, PageTitle) are needed; it is
 * never matched as a page slug, so it does not affect routing or the title.
 * @type {{PageName: string, PageTitle: string}}
 */
const OTHER_SETTLEMENTS_PAGE = {
    PageName: "/",
    PageTitle: "Other Settlements",
};

/**
 * Return the pages array with the "Other Settlements" link appended when the
 * current path is a settlement subsite; otherwise return it unchanged.
 *
 * @param {Array} pages - The docket's pages (siteInfoLang.Pages).
 * @param {string[]} docketSlugs - Configured docket slugs.
 * @param {string} pathname - The current pathname.
 * @returns {Array} The pages array, with the link appended on subsites.
 */
export function withOtherSettlementsLink(pages, docketSlugs, pathname) {
    if (!currentDocketSlug(pathname, docketSlugs)) {
        return pages;
    }
    return [...pages, OTHER_SETTLEMENTS_PAGE];
}

/**
 * Return the pages array without the "submit-claim" page, so the shared
 * Navigation renders neither the submit-claim nav link nor the "Submit Claim"
 * button. Used while the claim form is suppressed. Returns the same reference
 * when there is no submit-claim page.
 *
 * @param {Array} pages - The docket's pages (siteInfoLang.Pages).
 * @returns {Array} The pages array without the submit-claim entry.
 */
export function withoutSubmitClaim(pages) {
    if (!pages.some((page) => page.PageName === "submit-claim")) {
        return pages;
    }
    return pages.filter((page) => page.PageName !== "submit-claim");
}
